"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import { RoundResult } from "@/lib/types/common/GameState";
import { useMainMap } from "@/app/providers/MainMapContextProvider";
import Image from "next/image";
import { convertDistanceToYalms } from "@/lib/utils/coordinateConversion";
import { useGame } from "@/app/providers/GameContextProvider";
import { useTranslation } from "@/hooks/useTranslation";
import TooltipWithIconFFXIV from "@/app/components/TooltipWithIconFFXIV";
import { PRECISION_MIN_DISTANCE } from "@/lib/types/common/ScoringConstants";
import { Team } from "@/lib/types/common/Team";
import BarFFXIV from "@/app/components/BarFFXIV";
import { motion, animate, AnimatePresence } from "framer-motion";
import { CoopRole } from "@/lib/types/common/Player";
import { getTeamColorHex } from "@/lib/config/teams";
import PlayerAvatar, { calculateDuplicateNumbers } from "@/app/components/PlayerAvatar";
import { useIsMobile } from "@/hooks/useIsMobile";
import { AUDIO, useAudio } from "@/app/providers/AudioProvider";

interface CoopModePlayerResultsListProps {
    roundResult: RoundResult;
    onHoverMapPlayer?: (playerId: string | null) => void;
    onClickMapPlayer?: (playerId: string | null) => void;
    lockedMapPlayer?: string | null;
}

interface PlayerResultData {
    sessionId: string;
    name: string;
    guessedOnCorrectMap: boolean;
    distance?: number;
    guessedMapName?: string;
    guessedMapId?: string;
    roundScore: number;
    totalScore: number;
    hasGuessed: boolean;
    avatarId: string;
    playerNumber: number;
    isEliminated: boolean;
    teamId: string;
    coopRole: CoopRole;
}

interface TeamData {
    team: Team;
    players: PlayerResultData[];
    roundTotalScore: number;
    previousTotalScore: number;
}

// Calculate distance between two points
const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
};

export default function CoopModePlayerResultsList({
    roundResult,
    onHoverMapPlayer,
    onClickMapPlayer,
    lockedMapPlayer,
}: CoopModePlayerResultsListProps) {
    const { gameState, room } = useGame();
    const { allMaps } = useMainMap();
    const { locale } = useTranslation();
    const isMobile = useIsMobile();

    // Animation state
    const [animatedScores, setAnimatedScores] = useState<Record<string, number>>({});
    const [animatedBarWidths, setAnimatedBarWidths] = useState<Record<string, number>>({});
    const [animatedRoundScores, setAnimatedRoundScores] = useState<Record<string, number | undefined>>({});
    const hasAnimatedRef = useRef(false);

    // Process player results and organize by team
    const teamData: (TeamData | null)[] = useMemo(() => {
        if (!gameState) return [null, null, null, null];

        const playerResults: PlayerResultData[] = Array.from(roundResult.playerResults.entries()).map(([sessionId, player]) => {
            const guess = player.guesses.get(roundResult.roundNumber.toString());
            const roundScore = player.scores[roundResult.roundNumber - 1] || 0;
            const currentPlayer = gameState.players?.get(sessionId);
            const coopRole = (currentPlayer?.coopRole as CoopRole) || CoopRole.NONE;

            if (!guess) {
                return {
                    sessionId,
                    name: player.name,
                    guessedOnCorrectMap: false,
                    roundScore,
                    totalScore: player.totalScore,
                    hasGuessed: false,
                    avatarId: player.avatarId,
                    playerNumber: player.playerNumber || 0,
                    isEliminated: player.isEliminated || false,
                    teamId: currentPlayer?.teamId || '',
                    coopRole,
                };
            }

            const guessedOnCorrectMap = guess.mapId === roundResult.photosphere.mapId;
            let distance: number | undefined;
            let guessedMapName: string | undefined;

            if (guessedOnCorrectMap) {
                distance = calculateDistance(
                    guess.x,
                    guess.y,
                    roundResult.photosphere.coord.x,
                    roundResult.photosphere.coord.y
                ) - PRECISION_MIN_DISTANCE;
            } else {
                const guessedMap = allMaps.find(map => map.id === guess.mapId);
                guessedMapName = guessedMap ? guessedMap.name[locale as keyof typeof guessedMap.name] : undefined;
            }

            return {
                sessionId,
                name: player.name,
                guessedOnCorrectMap,
                distance,
                guessedMapName,
                guessedMapId: guess.mapId,
                roundScore,
                totalScore: player.totalScore,
                hasGuessed: true,
                avatarId: player.avatarId,
                playerNumber: player.playerNumber || 0,
                isEliminated: player.isEliminated || false,
                teamId: currentPlayer?.teamId || '',
                coopRole,
            };
        });

        // Organize by team - always create 4 slots
        const teamsMap = new Map<string, TeamData>();

        for (const team of Array.from(gameState.teams)) {
            teamsMap.set(team.id, {
                team,
                players: [],
                roundTotalScore: 0,
                previousTotalScore: 0,
            });
        }

        for (const player of playerResults) {
            const teamData = teamsMap.get(player.teamId);
            if (teamData) {
                teamData.players.push(player);
                // Only count scores from MAP player to avoid double counting
                // (both players have same scores but we should only count once per team)
                if (player.coopRole === CoopRole.MAP) {
                    teamData.roundTotalScore += player.roundScore;
                    // Previous total score is current total minus this round's score
                    teamData.previousTotalScore += (player.totalScore - player.roundScore);
                    console.log(`Team ${teamData.team.name} MAP player ${player.name}: previousTotalScore=${teamData.previousTotalScore}, roundScore=${player.roundScore}, totalScore=${player.totalScore}`);
                }
            }
        }

        // Sort players within each team: MAP role first, then PHOTOSPHERE
        Array.from(teamsMap.values()).forEach((data) => {
            data.players.sort((a: PlayerResultData, b: PlayerResultData) => {
                if (a.coopRole === CoopRole.MAP && b.coopRole !== CoopRole.MAP) return -1;
                if (a.coopRole !== CoopRole.MAP && b.coopRole === CoopRole.MAP) return 1;
                return 0;
            });
        });

        // Convert to array sorted by team number, pad to 4 slots
        const sortedTeams = Array.from(teamsMap.values())
            .filter(data => data.players.length > 0)
            .sort((a, b) => a.team.teamNumber - b.team.teamNumber);

        // Always return 4 slots (null for empty)
        const result: (TeamData | null)[] = [null, null, null, null];
        sortedTeams.forEach((team, index) => {
            result[index] = team;
        });

        return result;
    }, [roundResult, gameState, allMaps, locale]);

    // Calculate max total score for bar percentage
    const maxTotalScore = useMemo(() => {
        if (gameState == null) return 0;
        return gameState?.currentRound * 1000;
    }, [gameState]);

    // Initialize animated scores and trigger animation
    useEffect(() => {
        if (!gameState || hasAnimatedRef.current) return;
        hasAnimatedRef.current = true;

        // Initialize with previous scores and bar widths
        teamData.forEach((data) => {
            if (data) {
                // Use team's actual total score from backend if available, otherwise calculated previous score
                const previousScore = data.team.totalScore ? (data.team.totalScore - data.roundTotalScore) : data.previousTotalScore;
                const previousBarWidth = (previousScore / maxTotalScore) * 100;

                setAnimatedScores(prev => ({ ...prev, [data.team.id]: previousScore }));
                setAnimatedBarWidths(prev => ({ ...prev, [data.team.id]: previousBarWidth }));
            }
        });

        // Initialize round scores immediately (same as PlayerResultsList)
        teamData.forEach((data) => {
            if (data) {
                setAnimatedRoundScores(prev => ({ ...prev, [data.team.id]: data.roundTotalScore }));
            }
        });

        // After delay, animate score increase and round score countdown simultaneously
        teamData.forEach((data) => {
            if (data) {
                const previousScore = data.team.totalScore ? (data.team.totalScore - data.roundTotalScore) : data.previousTotalScore;
                const targetScore = data.team.totalScore ? data.team.totalScore : (data.previousTotalScore + data.roundTotalScore);
                const targetBarWidth = (targetScore / maxTotalScore) * 100;

                // Animate total score number (starts at 1.8s)
                animate(previousScore, targetScore, {
                    duration: data.roundTotalScore / 500,
                    delay: 1.8,
                    onUpdate: (value) => {
                        setAnimatedScores(prev => ({ ...prev, [data.team.id]: Math.round(value) }));
                    },
                });

                // Animate bar width (starts at 1.8s)
                animate((previousScore / maxTotalScore) * 100, targetBarWidth, {
                    duration: data.roundTotalScore / 500,
                    delay: 1.8,
                    onUpdate: (value) => {
                        setAnimatedBarWidths(prev => ({ ...prev, [data.team.id]: value }));
                    },
                });

                // Animate round scores countdown to 0 (starts at 1.8s, same time as bar animation)
                animate(data.roundTotalScore, 0, {
                    duration: data.roundTotalScore / 500,
                    delay: 1.8,
                    onUpdate: (value) => {
                        setAnimatedRoundScores(prev => ({ ...prev, [data.team.id]: Math.floor(value)}) );
                    },
                    onComplete: () => {
                        setTimeout(() => {
                            setAnimatedRoundScores(prev => ({ ...prev, [data.team.id]: undefined }));
                        }, 500);
                    }
                });
            }
        });

        // No cleanup needed for framer-motion animate, so no-op cleanup
        return () => {};
    }, [roundResult.roundNumber, maxTotalScore, teamData]);

    if (!gameState || !room) return null;

    // Count active teams
    const activeTeams = teamData.filter(data => data !== null);
    const isSingleTeam = activeTeams.length === 1;

    return (
        <div className="relative px-4 md:px-0">
            <div className="absolute w-full translate-x-[-25px] h-full flex z-[1] blur-sm">
                <div className="w-1/2 h-full bg-gradient-to-l from-black/50 via-black/30 via-[88%] to-transparent"></div>
                <div className="w-1/2 h-full bg-gradient-to-r from-black/50 via-black/30 via-[88%] to-transparent"></div>
            </div>
            <div className="relative w-full flex flex-col gap-1 2xl:gap-4 text-white px-2 z-[2] mt-1 2xl:mt-4">
                {isMobile ? (
                    // Mobile: single column layout
                    <div className="flex flex-col gap-2 w-full">
                        {activeTeams.map((data, index) => (
                            <div key={index} className="w-full">
                                <TeamBlock
                                    teamData={data!}
                                    onHoverMapPlayer={onHoverMapPlayer}
                                    onClickMapPlayer={onClickMapPlayer}
                                    lockedMapPlayer={lockedMapPlayer}
                                    align="left"
                                    animatedScore={animatedScores[data!.team.id]}
                                    animatedBarWidth={animatedBarWidths[data!.team.id]}
                                    animatedRoundScore={animatedRoundScores[data!.team.id]}
                                    maxScore={maxTotalScore}
                                    isMobile={true}
                                />
                            </div>
                        ))}
                    </div>
                ) : isSingleTeam ? (
                    // Single team: center it
                    <div className="flex justify-center w-full max-w-[600px] mx-auto">
                        <TeamBlock
                            teamData={activeTeams[0]!}
                            onHoverMapPlayer={onHoverMapPlayer}
                            onClickMapPlayer={onClickMapPlayer}
                            lockedMapPlayer={lockedMapPlayer}
                            align="left"
                            animatedScore={animatedScores[activeTeams[0]!.team.id]}
                            animatedBarWidth={animatedBarWidths[activeTeams[0]!.team.id]}
                            animatedRoundScore={animatedRoundScores[activeTeams[0]!.team.id]}
                            maxScore={maxTotalScore}
                            isMobile={false}
                        />
                    </div>
                ) : (
                    // Multiple teams: 2x2 grid
                    [0, 2].map(rowStart => (
                        <div key={rowStart} className="flex gap-1 2xl:gap-4 w-full">
                            {teamData.slice(rowStart, rowStart + 2).map((data, index) => (
                                <div
                                    key={rowStart + index}
                                    className="basis-1/2 shrink-0"
                                >
                                    {data ? (
                                        <TeamBlock
                                            teamData={data!}
                                            onHoverMapPlayer={onHoverMapPlayer}
                                            onClickMapPlayer={onClickMapPlayer}
                                            lockedMapPlayer={lockedMapPlayer}
                                            align={index % 2 === 0 ? 'left' : 'right'}
                                            animatedScore={animatedScores[data!.team.id]}
                                            animatedBarWidth={animatedBarWidths[data!.team.id]}
                                            animatedRoundScore={animatedRoundScores[data!.team.id]}
                                            maxScore={maxTotalScore}
                                            isMobile={false}
                                        />
                                    ) : (
                                        <div className="h-full w-full opacity-20 flex items-center justify-center">
                                            {/* Empty team slot */}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

interface TeamBlockProps {
    teamData: TeamData;
    onHoverMapPlayer?: (playerId: string | null) => void;
    onClickMapPlayer?: (playerId: string | null) => void;
    lockedMapPlayer?: string | null;
    align?: 'left' | 'right';
    animatedScore?: number;
    animatedBarWidth?: number;
    animatedRoundScore?: number;
    maxScore: number;
    isMobile?: boolean;
}

function TeamBlock({
    teamData,
    onHoverMapPlayer,
    onClickMapPlayer,
    lockedMapPlayer,
    align = 'left',
    animatedScore,
    animatedBarWidth,
    animatedRoundScore,
    maxScore,
    isMobile = false,
}: TeamBlockProps) {
    const { gameState } = useGame();
    const { team, players, roundTotalScore } = teamData;
    const { t } = useTranslation();
        const { play } = useAudio();


    const displayTotalScore = animatedScore ?? (team.totalScore || (teamData.previousTotalScore + roundTotalScore));
    const scorePercent = animatedBarWidth ?? ((displayTotalScore / maxScore) * 100);
    console.log("displayTotalScore", displayTotalScore, "maxScore", maxScore, "scorePercent", scorePercent, "team.totalScore", team.totalScore);

    // Calculate duplicate numbers for avatars within this team
    const duplicateNumbers = useMemo(() => {
        return calculateDuplicateNumbers(players.map(p => ({ sessionId: p.sessionId, avatarId: p.avatarId })));
    }, [players]);

    // Shadow logic for scrollable area
    const [showTopShadow, setShowTopShadow] = React.useState(false);
    const [showBottomShadow, setShowBottomShadow] = React.useState(false);
    const scrollRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const handleScroll = () => {
            setShowTopShadow(el.scrollTop > 0 && players.length > 4);
            setShowBottomShadow(el.scrollTop + el.clientHeight < el.scrollHeight && players.length > 4);
        };
        handleScroll(); // Initial
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [players.length]);

    return (
        <motion.div
            className="text-white w-full h-full relative flex flex-col md:mx-3"
        >
            {/* Background blur */}
            {!isMobile && (
                <div className="absolute z-10 top-0 w-full h-full left-0 blur-sm flex opacity-30">
                    <div
                        className="px-0 2xl:px-10 w-1/2 h-full"
                        style={{
                            background: `linear-gradient(to right, transparent 0%, rgb(0,0,0) 50%, rgb(0,0,0) 100%)`
                        }}
                    ></div>
                    <div
                        className="px-0 2xl:px-10 w-1/2 h-full"
                        style={{
                            background: `linear-gradient(to left, transparent 0%, transparent 0%, rgb(0,0,0) 50%, rgb(0,0,0) 100%)`
                        }}
                    ></div>
                </div>
            )}

            {/* Team Header */}
            <div className={`z-20 relative text-[20px] 2xl:text-[28px] h-3 2xl:h-5 font-semibold font-orgon-plan uppercase whitespace-nowrap flex gap-2 items-center ${align === 'left' ? 'justify-start' : 'flex-row-reverse'}`}>
                <div className="absolute z-10 top-0 w-full h-full left-0 blur-sm flex opacity-40">
                    <div
                        className="px-0 2xl:px-10 w-1/2 h-full"
                        style={{
                            background: `linear-gradient(to right, transparent 0%, ${getTeamColorHex(team.color)}b3 ${align === 'left' ? '20%' : '50%'}, ${getTeamColorHex(team.color)}b3 100%)`
                        }}
                    ></div>
                    <div
                        className="px-0 2xl:px-10 w-1/2 h-full"
                        style={{
                            background: `linear-gradient(to left, transparent 0%, ${getTeamColorHex(team.color)}b3 ${align === 'right' ? '20%' : '50%'}, ${getTeamColorHex(team.color)}b3 100%)`
                        }}
                    ></div>
                </div>
                <div
                    className="relative z-10 w-[14px] h-[14px] 2xl:w-[18px] 2xl:h-[18px] rounded border border-yellow-300 shadow-[inset_0_0_6px_#000]"
                    style={{ backgroundColor: getTeamColorHex(team.color) }}
                />
                <span
                    className={`relative z-10 text-shadow shadow-yellow-800`}
                    style={{ transform: 'rotateX(60deg)' }}
                >
                    {team.name}
                </span>
            </div>

            {/* Content */}
            <div className={`z-20 relative w-full mt-1 grow flex transition-all duration-500`}>
                <div className="flex-1 flex flex-col justify-between h-full">
                    <div className="relative grow">
                        {/* Top shadow */}
                        <div
                            className="pointer-events-none absolute top-0 left-0 w-full h-8 z-20 bg-gradient-to-b from-black/40 to-transparent duration-300"
                            style={showTopShadow ? {} : { opacity: 0 }}
                        />
                        {/* Bottom shadow */}
                        <div
                            className="pointer-events-none absolute bottom-0 left-0 w-full h-8 z-20 bg-gradient-to-t from-black/40 to-transparent duration-300"
                            style={showBottomShadow ? {} : { opacity: 0 }}
                        />

                        <div
                            ref={scrollRef}
                            className={`flex gap-2 scrollbar-hide max-h-none md:max-h-[50px] 2xl:max-h-[125px] ${players.length > 4 ? '2xl:overflow-y-auto' : '2xl:overflow-hidden'} ${players.length > 2 ? 'overflow-y-auto' : 'overflow-hidden'} ${align === 'left' ? '' : 'flex-row-reverse'}`}
                        >
                            {/* Players Column */}
                            <div className="flex flex-col gap-0.5 2xl:gap-1 grow h-fit w-[100px] md:w-[150px] max-w-[150px] 2xl:w-[230px] 2xl:max-w-none">
                                {players.map((player) => (
                                    <div
                                        key={player.sessionId}
                                        className={`h-[20px] 2xl:h-[25px] flex gap-2 items-center ${align === 'left' ? '' : 'flex-row-reverse'}`}
                                    >
                                        <div className="w-[20px] h-[20px] 2xl:w-[25px] 2xl:h-[25px] relative">
                                            <PlayerAvatar
                                                avatarId={player.avatarId}
                                                duplicateNumber={duplicateNumbers.get(player.sessionId) || 0}
                                                size={isMobile ? 20 : 25}
                                                className="w-full h-full"
                                                isLeader={gameState?.gameMasterId === player.sessionId}
                                                leaderIconSize={16}
                                            />
                                        </div>
                                        <div className={`flex items-center gap-2 ${align === 'left' ? '' : 'flex-row-reverse'}`}>
                                            <div className="text-shadow shadow-blue-800 font-myriad text-sm 2xl:text-lg whitespace-nowrap max-w-[80px] md:max-w-[90px] 2xl:max-w-none truncate">{player.name}</div>
                                            <div className="w-5 h-5 2xl:w-6 2xl:h-6 flex items-center justify-center">
                                                {gameState?.players?.get(player.sessionId)?.isReady && (
                                                    <Image
                                                        src='/checkmark.png'
                                                        alt="Check Icon"
                                                        height={24}
                                                        width={24}
                                                        className="translate-y-[0px]"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Guesses Column - Only show for MAP role */}
                            <div className="flex flex-col gap-0.5 2xl:gap-1 grow h-fit w-[100px] md:w-[150px] 2xl:w-[230px] max-w-[130px] md:max-w-[150px] 2xl:max-w-none">
                                {players.map((player) => (
                                    <div
                                        key={player.sessionId}
                                        className={`text-shadow shadow-blue-600 font-myriad text-sm 2xl:text-lg whitespace-nowrap flex items-center h-[20px] 2xl:h-[25px] relative ${align === 'left' ? '' : 'flex-row-reverse'}`}
                                    >
                                        {/* Only show guess info for MAP role */}
                                        {player.coopRole === CoopRole.MAP && (
                                            <>
                                                {player.hasGuessed && player.guessedOnCorrectMap && (
                                                    player.roundScore === 1000 ? (
                                                        t("play.onTheSpot")
                                                    ) : (
                                                        <div className="flex gap-3 items-center relative">
                                                            {t("play.yalmsAway", { distance: convertDistanceToYalms(player.distance ?? 0) })}
                                                            <TooltipWithIconFFXIV
                                                                text={t("play.accuracyTooltip")}
                                                                size="small"
                                                                className="text-sm 2xl:text-lg"
                                                            />
                                                        </div>
                                                    )
                                                )}
                                                {!player.hasGuessed && (
                                                    <span>{t("play.noGuess")}</span>
                                                )}
                                                {player.hasGuessed && !player.guessedOnCorrectMap && (
                                                    <div className="flex gap-2 items-center">
                                                        <span>{player.guessedMapName}</span>
                                                        {onHoverMapPlayer && onClickMapPlayer && (
                                                            <div
                                                                onMouseEnter={() => {
                                                                    play(AUDIO.HOVER);
                                                                    if (!lockedMapPlayer) {
                                                                        onHoverMapPlayer(player.sessionId);
                                                                    }
                                                                }}
                                                                onMouseLeave={() => onHoverMapPlayer(null)}
                                                                onClick={() => {
                                                                    play(AUDIO.CLICK);
                                                                    if (lockedMapPlayer === player.sessionId) {
                                                                        onClickMapPlayer(null);
                                                                    } else {
                                                                        onClickMapPlayer(player.sessionId);
                                                                    }
                                                                }}
                                                                className={`cursor-pointer flex items-center transition-all w-[20px] h-[20px] 2xl:w-[25px] 2xl:h-[25px] duration-200 ${lockedMapPlayer === player.sessionId
                                                                    ? 'scale-110 drop-shadow-[0_0_8px_rgba(37,99,235,0.8)]'
                                                                    : 'hover:scale-105'
                                                                    }`}
                                                            >
                                                                <Image
                                                                    src="/map_icon.png"
                                                                    alt="View Map"
                                                                    width={35}
                                                                    height={35}
                                                                    className={`${lockedMapPlayer === player.sessionId
                                                                        ? 'brightness-125 saturate-150'
                                                                        : ''
                                                                        }`}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {/* For PHOTOSPHERE role, show nothing (empty space) */}
                                    </div>
                                ))}
                            </div>
                            {/* Scores - Only show for MAP role (since both share same score) */}
                            <div className="flex flex-col gap-0.5 2xl:gap-1 h-fit w-[40px] md:w-[60px] 2xl:w-[88px]">
                                {players.map((player) => (
                                    <div
                                        key={player.sessionId}
                                        className={`h-[20px] 2xl:h-[25px] text-shadow shadow-blue-800 font-contrax text-xs md:text-sm 2xl:text-md whitespace-nowrap flex items-center ${align === 'left' ? '' : 'justify-end'}`}
                                    >
                                        {player.coopRole === CoopRole.MAP && player.roundScore}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Score Bar and total score */}
                    <div className={`relative flex md:justify-between items-center gap-2 mt-1 2xl:mt-4 ${align === 'left' ? '' : 'flex-row-reverse'}`}>
                        <div className="relative w-[287px] md:w-[300px] 2xl:w-[500px]">
                            <AnimatePresence>
                                {animatedRoundScore !== undefined && (
                                    <motion.span
                                        initial={{ opacity: 0, y: 5, scale: 0.8 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, transition: { delay: 0.3 } }}
                                        transition={{ duration: 0.1, ease: "easeOut", delay: 1 }}
                                        className="absolute top-[-17px] right-0 text-xs text-yellow-200 font-contrax text-shadow-border shadow-amber-800/50"
                                    >
                                        +{animatedRoundScore}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                            <BarFFXIV
                                width="w-full"
                                height="h-2 2xl:h-3.5"
                                percent={scorePercent}
                                number={displayTotalScore}
                                numberSize="text-xs 2xl:text-xl"
                                numberAlign={align === 'left' ? 'right' : 'left'}
                                startFrom={align === 'left' ? 'left' : 'right'}
                                numberText={Math.round(displayTotalScore) + " pts"}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
